import React from 'react';
import { Link } from 'react-router-dom';
import { List, useRefresh, useDataProvider } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
} from '@mui/material';
import { Button } from '../components/ui/button';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { AvatarName } from '../components/ui/data-cells';
import { toastSuccess, toastError } from '../lib/toast';
import { CustomDataProvider } from '../types/dataProvider';
import { cop } from './payments';

interface LedgerItem {
  id: number; // = trainer_id
  trainer_id: number;
  trainer_name?: string;
  trainer_email?: string;
  payments_count: number;
  gross_owed: number;
  platform_fee_total: number;
  net_owed: number;
}

const SettleDialog = ({ row, onClose, onSuccess }: { row: LedgerItem; onClose: () => void; onSuccess: () => void }) => {
  const [ref, setRef] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const dataProvider = useDataProvider<CustomDataProvider>();
  const submit = async () => {
    setSubmitting(true);
    try {
      await dataProvider.settlePayout({ trainerId: row.trainer_id, transfer_reference: ref, admin_notes: notes });
      toastSuccess(`Settled ${cop(row.net_owed)} to ${row.trainer_name}`);
      onSuccess();
      onClose();
    } catch (e: any) {
      toastError(e?.message || 'Settle failed');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Settle payout · {row.trainer_name}</DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-4">
          <p className="text-sm text-ink-2">
            Records a manual transfer of <strong className="text-ink">{cop(row.net_owed)}</strong> ({row.payments_count} payments) and marks them paid out.
          </p>
          <MuiTextField fullWidth label="Transfer reference (Bancolombia / Wompi)" value={ref} onChange={(e) => setRef(e.target.value)} />
          <MuiTextField fullWidth multiline rows={2} label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outline" disabled={submitting}>Cancel</Button>
        <Button onClick={submit} className="bg-black text-white hover:bg-ink" disabled={submitting}>{submitting ? 'Settling…' : 'Mark settled'}</Button>
      </DialogActions>
    </Dialog>
  );
};

const ListBody = () => {
  const refresh = useRefresh();
  const [settle, setSettle] = React.useState<LedgerItem | null>(null);
  const columns: DataColumn<LedgerItem>[] = [
    {
      key: 'trainer',
      header: 'Trainer',
      cell: (r) => (
        <Link to={`/trainers/${r.trainer_id}/show`} onClick={(e) => e.stopPropagation()}>
          <AvatarName name={r.trainer_name || `#${r.trainer_id}`} subtext={r.trainer_email} />
        </Link>
      ),
    },
    { key: 'payments_count', header: 'Payments', align: 'right', cell: (r) => <span className="tabular-nums">{r.payments_count}</span> },
    { key: 'gross_owed', header: 'Gross', align: 'right', cell: (r) => <span className="tabular-nums text-ink-2">{cop(r.gross_owed)}</span> },
    { key: 'platform_fee_total', header: 'Fee', align: 'right', cell: (r) => <span className="tabular-nums text-ink-2">{cop(r.platform_fee_total)}</span> },
    { key: 'net_owed', header: 'Owed', align: 'right', cell: (r) => <span className="tabular-nums font-bold text-ink">{cop(r.net_owed)}</span> },
    {
      key: 'action',
      header: '',
      align: 'right',
      cell: (r) => (
        <Button onClick={(e: any) => { e.stopPropagation(); setSettle(r); }} className="h-8 bg-black text-white hover:bg-ink">
          Settle
        </Button>
      ),
    },
  ];
  return (
    <>
      <ListScreen>
        <DataTable columns={columns} emptyMessage="Nothing owed — all trainers settled." />
        <div className="pt-1">
          <ReactAdminPagination />
        </div>
      </ListScreen>
      {settle && <SettleDialog row={settle} onClose={() => setSettle(null)} onSuccess={refresh} />}
    </>
  );
};

export const PayoutList = (props: any) => (
  <List {...props} actions={false} empty={false} pagination={false} perPage={50} component="div">
    <ListBody />
  </List>
);
